import React from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  ReferenceInput,
  SelectInput,
  required,
  email,
  useNotify,
  useRecordContext,
} from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Divider,
} from '@mui/material';
import {
  Factory as FactoryIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import RolePermissionManager from '../../components/RolePermissionManager';
import { useRolePermissionEditTransform } from '../../hooks/useRolePermissionTransform';

// Validadores personalizados
const validateRequired = required('Este campo es obligatorio');
const validateEmail = email('Debe ser un email válido');
const validatePhone = (value: string) => {
  if (value && !/^[\d\s\-\+\(\)]+$/.test(value)) {
    return 'Formato de teléfono inválido';
  }
  return undefined;
};

// Componente de título personalizado
const EditTitle = () => {
  const record = useRecordContext();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
      <EditIcon color='primary' sx={{ fontSize: 32 }} />
      <Typography variant='h4' component='h1' fontWeight='bold'>
        Editar Planta: {record?.plantName || 'Cargando...'}
      </Typography>
    </Box>
  );
};

const PlantsEdit = () => {
  const notify = useNotify();
  const { transformForSave } = useRolePermissionEditTransform();

  const handleSuccess = () => {
    notify('Planta actualizada exitosamente', { type: 'success' });
  };

  const handleError = (error: any) => {
    console.error('Error updating plant:', error);
    notify('Error al actualizar la planta. Por favor intente nuevamente.', {
      type: 'error',
    });
  };
  return (
    <Edit
      transform={transformForSave}
      sx={{
        '& .RaEdit-card': {
          boxShadow: 'none',
          backgroundColor: 'transparent',
        },
      }}
    >
      <Box sx={{ padding: 3, maxWidth: 1200, margin: '0 auto' }}>
        <EditTitle />{' '}
        <SimpleForm
          sx={{
            '& .RaSimpleForm-toolbar': {
              marginTop: 3,
              padding: 2,
              backgroundColor: 'background.paper',
              borderRadius: 2,
              boxShadow: 2,
            },
          }}
        >
          <Grid container spacing={3}>
            {/* Información Principal */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: 'fit-content',
                  boxShadow: 3,
                  borderRadius: 2,
                  transition: 'box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <FactoryIcon color='primary' />
                    <Typography variant='h6' fontWeight='bold'>
                      Información de la Planta
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <TextInput
                    source='plantName'
                    label='Nombre de la Planta'
                    validate={validateRequired}
                    fullWidth
                    sx={{ mb: 2 }}
                    helperText='Ingrese el nombre identificativo de la planta'
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Información de Contacto */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: 'fit-content',
                  boxShadow: 3,
                  borderRadius: 2,
                  transition: 'box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <PhoneIcon color='primary' />
                    <Typography variant='h6' fontWeight='bold'>
                      Información de Contacto
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <TextInput
                    source='clientPhoneContact'
                    label='Teléfono de Contacto'
                    validate={validatePhone}
                    fullWidth
                    sx={{ mb: 2 }}
                    helperText='Número de teléfono para contacto'
                  />

                  <TextInput
                    source='clientEmail'
                    label='Email de Contacto'
                    validate={validateEmail}
                    fullWidth
                    sx={{ mb: 2 }}
                    helperText='Dirección de email para contacto'
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Cliente Asociado */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: 'fit-content',
                  boxShadow: 3,
                  borderRadius: 2,
                  transition: 'box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <BusinessIcon color='primary' />
                    <Typography variant='h6' fontWeight='bold'>
                      Cliente Asociado
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <ReferenceInput
                    source='clientId'
                    reference='clients'
                    sort={{ field: 'clientName', order: 'ASC' }}
                  >
                    <SelectInput
                      optionText='clientName'
                      optionValue='id'
                      label='Cliente'
                      validate={validateRequired}
                      fullWidth
                      helperText='Seleccione el cliente propietario de esta planta'
                    />
                  </ReferenceInput>
                </CardContent>
              </Card>
            </Grid>

            {/* Componente Universal de Roles y Permisos */}
            <Grid item xs={12}>
              <RolePermissionManager
                title="Permisos de la Planta"
                subtitle="Modificar roles y permisos de acceso para esta planta"
                helperText="Los usuarios con diferentes roles tendrán diferentes niveles de acceso a la información y funcionalidades de esta planta."
              />
            </Grid>
          </Grid>
        </SimpleForm>
      </Box>
    </Edit>
  );
};

export default PlantsEdit;
